/**
 * SenderStep — first step in the wizard.
 *
 * Two entry paths:
 *   1. Search the customer database via the autocomplete and pick an existing
 *      customer — we then fetch their primary address and prefill the form.
 *   2. Click "Create new customer" to open a blank form. As the operator
 *      types the phone or email we run a live duplicate-check; if a match is
 *      found we surface a small panel offering one-click "use this customer"
 *      so the operator doesn't accidentally create a second record for
 *      someone already on file.
 *
 * The operator can ignore the duplicate hint and proceed; we leave the
 * decision with them. Mail submit doesn't create a customer record today —
 * the sender block is denormalised onto the mail item — so the worst case
 * is a stale duplicate hint, never a blocked workflow.
 */
import { type JSX, useEffect, useRef, useState } from 'react';

import { listCustomerAddresses, lookupCustomers } from '../../lib/api/customers';
import type { CustomerResponse, PartyPayload } from '../../lib/api/types';

import { CustomerLookupAutocomplete } from './CustomerLookupAutocomplete';
import { PartyForm } from './PartyForm';
import { SenderSchema } from './validators/sender.zod';

interface SenderStepProps {
  value: Partial<PartyPayload>;
  onChange: (next: Partial<PartyPayload>) => void;
  onContinue: () => void;
}

const DUP_DEBOUNCE_MS = 500;

export function SenderStep({ value, onChange, onContinue }: SenderStepProps): JSX.Element {
  const [showForm, setShowForm] = useState(value.name !== undefined && value.name.length > 0);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressNotice, setAddressNotice] = useState<string | null>(null);
  /** Whether the user explicitly chose to create-new (vs. picked from search).
   *  When true, we run live duplicate detection on phone/email. */
  const [creatingNew, setCreatingNew] = useState(false);
  const [duplicates, setDuplicates] = useState<readonly CustomerResponse[]>([]);
  const dupReqIdRef = useRef(0);

  const handleSelectCustomer = async (customer: CustomerResponse): Promise<void> => {
    // 1. Stamp the obvious basics immediately so the UI feels responsive.
    onChange({
      name: customer.fullName,
      phone: customer.primaryPhone,
      email: customer.primaryEmail ?? undefined,
      addressLine1: value.addressLine1 ?? '',
      city: value.city ?? '',
      state: value.state ?? '',
      countryCode: 'NG',
    });
    setShowForm(true);
    setCreatingNew(false);
    setDuplicates([]);
    setAddressNotice(null);

    // 2. Fetch the customer's address book and prefer the primary; fall back
    //    to the first if no primary flag is set. Failures are swallowed —
    //    the form still works, just without the prefill.
    setAddressLoading(true);
    try {
      const addresses = await listCustomerAddresses(customer.id);
      const primary = addresses.find((a) => a.isPrimary) ?? addresses[0];
      if (primary) {
        onChange({
          name: customer.fullName,
          phone: customer.primaryPhone,
          email: customer.primaryEmail ?? undefined,
          addressLine1: primary.line1,
          addressLine2: primary.line2 ?? undefined,
          city: primary.city,
          state: primary.state,
          postalCode: primary.postalCode ?? undefined,
          countryCode: primary.countryCode,
          geoCountryId: primary.geoCountryId ?? undefined,
          geoCountryName: primary.geoCountryName ?? undefined,
          geoStateId: primary.geoStateId ?? undefined,
          geoStateName: primary.geoStateName ?? undefined,
          geoLgaId: primary.geoLgaId ?? undefined,
          geoLgaName: primary.geoLgaName ?? undefined,
          geoWardId: primary.geoWardId ?? undefined,
          geoWardName: primary.geoWardName ?? undefined,
        });
        setAddressNotice(
          primary.isPrimary
            ? 'Primary address loaded — edit below if this parcel is being sent from elsewhere.'
            : `Loaded the customer's saved address (${primary.label || 'unlabelled'}) — edit below if needed.`,
        );
      } else {
        setAddressNotice('This customer has no saved address yet. Fill it in below.');
      }
    } catch {
      setAddressNotice("Couldn't load the customer's saved address. Fill it in below.");
    } finally {
      setAddressLoading(false);
    }
  };

  const handleCreateNew = (): void => {
    // Don't reset the form if the operator already typed something — they
    // may have used the dropdown's "+ Create new walk-in" first; switching
    // entry paths shouldn't erase their work.
    setShowForm(true);
    setCreatingNew(true);
    setAddressNotice(null);
  };

  // Live duplicate detection. Only runs when the operator is on the create-new
  // path. Looks up by phone OR email (whichever has at least 3 chars), debounced.
  useEffect(() => {
    if (!creatingNew) {
      setDuplicates([]);
      return;
    }
    const phone = value.phone?.trim() ?? '';
    const email = value.email?.trim() ?? '';
    const queries: { phone?: string; email?: string }[] = [];
    if (phone.length >= 4) queries.push({ phone });
    if (email.length >= 4 && email.includes('@')) queries.push({ email });
    if (queries.length === 0) {
      setDuplicates([]);
      return;
    }

    const reqId = ++dupReqIdRef.current;
    const handle = setTimeout(async () => {
      try {
        const responses = await Promise.all(
          queries.map((q) => lookupCustomers({ ...q, pageSize: 5 })),
        );
        if (reqId !== dupReqIdRef.current) return;
        // Dedup across phone + email matches by customer id.
        const byId = new Map<string, CustomerResponse>();
        for (const r of responses) for (const item of r.items) byId.set(item.id, item);
        setDuplicates(Array.from(byId.values()));
      } catch {
        if (reqId !== dupReqIdRef.current) return;
        setDuplicates([]);
      }
    }, DUP_DEBOUNCE_MS);

    return () => clearTimeout(handle);
  }, [creatingNew, value.phone, value.email]);

  const result = SenderSchema.safeParse(value);
  const isValid = result.success;

  return (
    <section className="space-y-4" data-testid="sender-step">
      <header>
        <h2 className="text-xl font-semibold text-slate-900">Sender</h2>
        <p className="text-sm text-slate-600">
          Search for an existing customer or create a new one.
        </p>
      </header>

      <div className="flex flex-col sm:flex-row sm:items-stretch sm:gap-2 gap-2">
        <div className="flex-1">
          <CustomerLookupAutocomplete
            onSelect={(c) => {
              void handleSelectCustomer(c);
            }}
            onCreateNew={handleCreateNew}
            placeholder="Search sender by phone or name"
          />
        </div>
        <button
          type="button"
          onClick={handleCreateNew}
          data-testid="sender-create-new-button"
          className="rounded bg-white border border-nipost-600 px-3 py-2 text-sm font-semibold text-nipost-700 hover:bg-nipost-50 whitespace-nowrap"
        >
          + Create new customer
        </button>
      </div>

      {addressLoading ? (
        <p className="text-xs text-slate-600" data-testid="sender-address-loading">
          Loading saved address…
        </p>
      ) : null}
      {addressNotice ? (
        <p
          className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2"
          data-testid="sender-address-notice"
        >
          {addressNotice}
        </p>
      ) : null}

      {creatingNew && duplicates.length > 0 ? (
        <div
          className="rounded border border-amber-300 bg-amber-50 p-3 space-y-2"
          data-testid="sender-duplicate-warning"
        >
          <p className="text-xs font-semibold text-amber-900">
            We found {duplicates.length} customer{duplicates.length === 1 ? '' : 's'} with a matching phone or email. Use an existing record?
          </p>
          <ul className="space-y-1">
            {duplicates.map((c) => (
              <li
                key={c.id}
                className="flex items-center justify-between gap-2 rounded border border-amber-200 bg-white px-2 py-1.5"
              >
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-slate-900 truncate">{c.fullName}</p>
                  <p className="text-[11px] text-slate-600 truncate">
                    {c.primaryPhone}
                    {c.primaryEmail ? ` · ${c.primaryEmail}` : ''}
                    {' · '}
                    <span className="font-mono">{c.customerNumber}</span>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    void handleSelectCustomer(c);
                  }}
                  data-testid={`sender-use-existing-${c.id}`}
                  className="rounded bg-nipost-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-nipost-700 whitespace-nowrap"
                >
                  Use this customer
                </button>
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-amber-800">
            Or keep typing to register a brand-new customer with these details.
          </p>
        </div>
      ) : null}

      {showForm && (
        <div className="rounded border border-slate-200 bg-white p-4">
          <PartyForm value={value} onChange={onChange} idPrefix="sender" />
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onContinue}
          disabled={!isValid}
          data-testid="sender-continue"
          className="rounded bg-nipost-600 px-4 py-2 text-sm font-semibold text-white disabled:bg-slate-300"
        >
          Continue
        </button>
      </div>
    </section>
  );
}
